/**
 * Settings page (`/settings`) — reference-list management.
 *
 * Settings now keeps reference/configuration lists only: supplier categories and incoterms.
 * Core business masters (suppliers and clients, including client departments) have
 * dedicated modules in the main navigation.
 *
 * Deactivation is the standard delete affordance — the server handles
 * soft-delete and refuses hard delete when references exist.
 */
import { useState } from 'react';
import { Edit3, Eye, FileText, Plus, Trash2 } from 'lucide-react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DataTable, type ColumnDef } from '@/components/ui/data-table';
import { Drawer } from '@/components/ui/drawer';
import { EmptyState } from '@/components/ui/empty-state';
import { FormField, Input, Textarea } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import { PageHeader } from '@/components/ui/page-header';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, type TabDef } from '@/components/ui/tabs';
import { MasterEntityDocumentsTab } from '@/components/attachments/master-entity-documents-tab';
import { WarningConfirmDialog } from '@/components/warning-confirm-dialog';
import { useCurrentUser } from '@/lib/auth';
import { usePageTitle } from '@/lib/use-page-title';
import {
  useCreateClient,
  useCreateDepartment,
  useCreateIncoterm,
  useCreateSupplier,
  useCreateSupplierCategory,
  useClientsListAll,
  useDeleteClient,
  useDeleteDepartment,
  useDeleteIncoterm,
  useDeleteSupplier,
  useDeleteSupplierCategory,
  useDepartmentsListAll,
  useIncotermsListAll,
  useSupplierCategoriesListAll,
  useSuppliersListAll,
  useUpdateClient,
  useUpdateDepartment,
  useUpdateIncoterm,
  useUpdateSupplier,
  useUpdateSupplierCategory,
  type ClientRecord,
  type DepartmentRecord,
  type IncotermRecord,
  type SupplierCategoryRecord,
  type SupplierRecord,
} from '@/lib/masters-queries';

type MasterTab = 'supplier-categories' | 'incoterms';
type StatusFilter = 'all' | 'active' | 'inactive';

const TABS: TabDef<MasterTab>[] = [
  { id: 'supplier-categories', label: 'Supplier Categories' },
  { id: 'incoterms', label: 'Incoterms' },
];

export function SettingsPage() {
  usePageTitle('Settings');
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  const [tab, setTab] = useState<MasterTab>('supplier-categories');

  // Spec §13 row 24 — "View Supplier/Client/Department Master | ✓ | ✓":
  // both roles can view masters. Row 25 — "Create/edit/deactivate masters
  // | ✓ | ✗": only Admin can mutate. Pre-E8 the whole page was Admin-only,
  // contradicting row 24. Mutation affordances inside each panel are now
  // gated on `isAdmin` instead of the page itself.

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={
          isAdmin
            ? 'Manage reference lists such as supplier categories and incoterms. Suppliers and clients are maintained in their own modules.'
            : 'View reference lists such as supplier categories and incoterms. Contact an admin to add or modify records.'
        }
      />
      <Tabs<MasterTab> tabs={TABS} activeId={tab} onChange={setTab} className="mb-6" />
      {tab === 'supplier-categories' ? <SupplierCategoriesPanel canMutate={isAdmin} /> : null}
      {tab === 'incoterms' ? <IncotermsPanel canMutate={isAdmin} /> : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Suppliers panel
// ---------------------------------------------------------------------------

export function SuppliersPanel({ canMutate }: { canMutate: boolean }) {
  const list = useSuppliersListAll();
  const categories = useSupplierCategoriesListAll();
  const del = useDeleteSupplier();
  const [edit, setEdit] = useState<SupplierRecord | null>(null);
  const [view, setView] = useState<SupplierRecord | null>(null);
  const [showNew, setShowNew] = useState(false);
  // E27 — view docs/comms for a master entity
  const [docsFor, setDocsFor] = useState<SupplierRecord | null>(null);
  // Patch 5 — pre-confirmation dialog state (replaces window.confirm)
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  if (list.isLoading || categories.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const allRows = list.data ?? [];
  const categoryRows = categories.data ?? [];
  const categoriesById = new Map(categoryRows.map((category) => [category.id, category]));
  const mainCategories = categoryRows.filter((category) => category.parentCategoryId === null && category.isActive);
  const visibleSubcategories = categoryRows.filter(
    (category) => category.parentCategoryId === categoryFilter && category.isActive,
  );
  const needle = q.trim().toLowerCase();
  const rows = allRows.filter((supplier) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' ? supplier.isActive : !supplier.isActive);
    const matchesCategory = !categoryFilter || supplier.categoryId === categoryFilter;
    const matchesSubcategory = !subcategoryFilter || supplier.subcategoryId === subcategoryFilter;
    const matchesSearch =
      !needle ||
      [
        supplier.name,
        supplier.contactPerson,
        supplier.officialPhone,
        supplier.personalPhone,
        supplier.address,
        supplier.notes,
        supplier.categoryId ? categoriesById.get(supplier.categoryId)?.name : null,
        supplier.subcategoryId ? categoriesById.get(supplier.subcategoryId)?.name : null,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    return matchesStatus && matchesCategory && matchesSubcategory && matchesSearch;
  });
  const columns: ColumnDef<SupplierRecord>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setView(r)}
          className="text-left font-medium text-primary underline-offset-2 hover:underline"
        >
          {r.name}
        </button>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      cell: (r) => {
        const category = r.categoryId ? categoriesById.get(r.categoryId) : null;
        const subcategory = r.subcategoryId ? categoriesById.get(r.subcategoryId) : null;
        return (
          <span className="text-secondary">
            {category ? category.name : '—'}
            {subcategory ? <span className="block text-xs text-secondary">{subcategory.name}</span> : null}
          </span>
        );
      },
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: (r) => (
        <div className="text-secondary">
          {r.contactPerson ?? '—'}
          {r.officialPhone ? (
            <span className="block text-xs text-secondary">{r.officialPhone}</span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
    {
      id: 'view',
      header: '',
      width: '40px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setView(r)}
          title="View supplier"
          aria-label={`View supplier ${r.name}`}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
        >
          <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ),
    },
    {
      id: 'docs',
      header: '',
      width: '40px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setDocsFor(r)}
          title="Documents & communications"
          aria-label={`Documents and communications for ${r.name}`}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
        >
          <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ),
    },
    // Actions column only renders for Admins (spec §13 row 25).
    ...(canMutate
      ? ([
          {
            id: 'actions',
            header: '',
            width: '90px',
            cell: (r: SupplierRecord) => (
              <RowActions
                onEdit={() => setEdit(r)}
                onDelete={() => {
                  setPendingConfirm({
                    title: 'Deactivate supplier?',
                    message: `Deactivate supplier "${r.name}"? They will no longer be selectable for new batches. Existing references keep the name snapshot.`,
                    onConfirm: () => del.mutate({ id: r.id, rowVersion: r.rowVersion }),
                  });
                }}
                deletable={r.isActive}
              />
            ),
          },
        ] as ColumnDef<SupplierRecord>[])
      : []),
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary">
          {rows.length} of {allRows.length} suppliers
        </span>
        {canMutate ? (
          <Button
            variant="primary"
            size="compact"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setShowNew(true)}
          >
            New supplier
          </Button>
        ) : null}
      </div>
      {del.error ? (
        <Alert tone="danger" className="mb-3">
          {del.error.message}
        </Alert>
      ) : null}
      <Card className="mb-3" bodyClassName="grid gap-3 md:grid-cols-[minmax(220px,1fr)_180px_180px_140px]">
        <FormField label="Search suppliers">
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Name, contact, phone, address…"
          />
        </FormField>
        <FormField label="Category">
          <Select
            value={categoryFilter}
            onChange={(event) => {
              setCategoryFilter(event.target.value);
              setSubcategoryFilter('');
            }}
          >
            <option value="">All categories</option>
            {mainCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Sub-category">
          <Select
            value={subcategoryFilter}
            onChange={(event) => setSubcategoryFilter(event.target.value)}
            disabled={!categoryFilter}
          >
            <option value="">All sub-categories</option>
            {visibleSubcategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Status">
          <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </Select>
        </FormField>
      </Card>
      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No suppliers yet"
            body="Suppliers appear here when added. They become selectable when creating order batches."
            action={
              canMutate ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add supplier
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<SupplierRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>

      {showNew ? (
        <SupplierModal categories={categoryRows} onClose={() => setShowNew(false)} />
      ) : null}
      {view ? (
        <SupplierDetailDrawer
          supplier={view}
          category={view.categoryId ? categoriesById.get(view.categoryId) ?? null : null}
          subcategory={view.subcategoryId ? categoriesById.get(view.subcategoryId) ?? null : null}
          canMutate={canMutate}
          onEdit={() => {
            setEdit(view);
            setView(null);
          }}
          onDocuments={() => {
            setDocsFor(view);
            setView(null);
          }}
          onClose={() => setView(null)}
        />
      ) : null}
      {edit ? (
        <SupplierModal existing={edit} categories={categoryRows} onClose={() => setEdit(null)} />
      ) : null}
      {docsFor ? (
        <Drawer
          open={true}
          onClose={() => setDocsFor(null)}
          title={`Documents — ${docsFor.name}`}
          width="wide"
        >
          <MasterEntityDocumentsTab kind="SUPPLIER" id={docsFor.id} name={docsFor.name} />
        </Drawer>
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel="Deactivate"
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}

function DetailItem({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-tertiary">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-sm text-primary">{value?.trim() ? value : '—'}</dd>
    </div>
  );
}

function SupplierDetailDrawer({
  supplier,
  category,
  subcategory,
  canMutate,
  onEdit,
  onDocuments,
  onClose,
}: {
  supplier: SupplierRecord;
  category: SupplierCategoryRecord | null;
  subcategory: SupplierCategoryRecord | null;
  canMutate: boolean;
  onEdit: () => void;
  onDocuments: () => void;
  onClose: () => void;
}) {
  return (
    <Drawer open={true} onClose={onClose} title={`Supplier — ${supplier.name}`} width="wide">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">{supplier.name}</h2>
            <div className="mt-1 flex flex-wrap gap-2">
              {supplier.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
              {category ? <Badge tone="neutral">{category.name}</Badge> : null}
              {subcategory ? <Badge tone="neutral">{subcategory.name}</Badge> : null}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="compact" onClick={onDocuments}>
              Documents
            </Button>
            {canMutate ? (
              <Button variant="primary" size="compact" onClick={onEdit}>
                Edit supplier
              </Button>
            ) : null}
          </div>
        </div>

        <Card bodyClassName="grid gap-4 md:grid-cols-2">
          <DetailItem label="Contact person" value={supplier.contactPerson} />
          <DetailItem label="Official phone" value={supplier.officialPhone} />
          <DetailItem label="Personal phone" value={supplier.personalPhone} />
          <DetailItem label="Category" value={category?.name} />
          <DetailItem label="Sub-category" value={subcategory?.name} />
          <DetailItem label="Address" value={supplier.address} />
          <div className="md:col-span-2">
            <DetailItem label="Notes" value={supplier.notes} />
          </div>
        </Card>

        <Card bodyClassName="grid gap-4 md:grid-cols-3">
          <DetailItem label="Created" value={new Date(supplier.createdAt).toLocaleString()} />
          <DetailItem label="Last updated" value={new Date(supplier.updatedAt).toLocaleString()} />
          <DetailItem label="Row version" value={String(supplier.rowVersion)} />
        </Card>
      </div>
    </Drawer>
  );
}

function SupplierModal({
  existing,
  categories,
  onClose,
}: {
  existing?: SupplierRecord;
  categories: SupplierCategoryRecord[];
  onClose: () => void;
}) {
  const create = useCreateSupplier();
  const update = useUpdateSupplier();
  const [name, setName] = useState(existing?.name ?? '');
  const [contactPerson, setContactPerson] = useState(existing?.contactPerson ?? '');
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState(existing?.subcategoryId ?? '');
  const [officialPhone, setOfficialPhone] = useState(existing?.officialPhone ?? '');
  const [personalPhone, setPersonalPhone] = useState(existing?.personalPhone ?? '');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const mainCategories = categories.filter((category) => category.parentCategoryId === null && category.isActive);
  const subcategoryOptions = categories.filter(
    (category) => category.parentCategoryId === categoryId && category.isActive,
  );
  const required = name.trim().length > 0;
  const submit = (): void => {
    if (!required) return;
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          name: name.trim(),
          contactPerson: contactPerson.trim() || null,
          officialPhone: officialPhone.trim() || null,
          personalPhone: personalPhone.trim() || null,
          address: address.trim() || null,
          categoryId: categoryId || null,
          subcategoryId: subcategoryId || null,
          notes: notes.trim() || null,
          rowVersion: existing.rowVersion,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          name: name.trim(),
          ...(contactPerson.trim() ? { contactPerson: contactPerson.trim() } : {}),
          ...(officialPhone.trim() ? { officialPhone: officialPhone.trim() } : {}),
          ...(personalPhone.trim() ? { personalPhone: personalPhone.trim() } : {}),
          ...(address.trim() ? { address: address.trim() } : {}),
          ...(categoryId ? { categoryId } : {}),
          ...(subcategoryId ? { subcategoryId } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { onSuccess: onClose },
      );
    }
  };

  const mutation = existing ? update : create;
  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit supplier' : 'New supplier'}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={mutation.isPending}
          >
            {existing ? 'Save changes' : 'Create supplier'}
          </Button>
        </>
      }
    >
      {mutation.error ? (
        <Alert tone="danger" className="mb-3">
          {mutation.error.message}
        </Alert>
      ) : null}
      <FormField label="Name" required>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>
      <FormField label="Contact person">
        <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Category">
          <Select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setSubcategoryId('');
            }}
          >
            <option value="">No category</option>
            {mainCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Sub-category">
          <Select
            value={subcategoryId}
            onChange={(e) => setSubcategoryId(e.target.value)}
            disabled={!categoryId}
          >
            <option value="">No sub-category</option>
            {subcategoryOptions.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Official phone">
          <Input value={officialPhone} onChange={(e) => setOfficialPhone(e.target.value)} />
        </FormField>
        <FormField label="Personal phone">
          <Input value={personalPhone} onChange={(e) => setPersonalPhone(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Address">
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
      </FormField>
      <FormField label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </FormField>
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// Supplier categories panel
// ---------------------------------------------------------------------------

function SupplierCategoriesPanel({ canMutate }: { canMutate: boolean }) {
  const list = useSupplierCategoriesListAll();
  const del = useDeleteSupplierCategory();
  const [edit, setEdit] = useState<SupplierCategoryRecord | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  if (list.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const rows = list.data ?? [];
  const categoriesById = new Map(rows.map((category) => [category.id, category]));
  const columns: ColumnDef<SupplierCategoryRecord>[] = [
    {
      id: 'name',
      header: 'Category / sub-category',
      cell: (r) => (
        <span className="font-medium">
          {r.parentCategoryId ? '↳ ' : ''}
          {r.name}
        </span>
      ),
    },
    {
      id: 'parent',
      header: 'Parent',
      cell: (r) => (
        <span className="text-secondary">
          {r.parentCategoryId ? categoriesById.get(r.parentCategoryId)?.name ?? '—' : 'Main category'}
        </span>
      ),
    },
    {
      id: 'notes',
      header: 'Notes',
      cell: (r) => <span className="text-secondary">{r.notes ?? '—'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
    ...(canMutate
      ? ([
          {
            id: 'actions',
            header: '',
            width: '90px',
            cell: (r: SupplierCategoryRecord) => (
              <RowActions
                onEdit={() => setEdit(r)}
                onDelete={() => {
                  setPendingConfirm({
                    title: 'Deactivate supplier category?',
                    message: `Deactivate supplier category "${r.name}"? It will no longer be selectable for suppliers. Existing suppliers keep their current reference unless the category is deleted.`,
                    onConfirm: () => del.mutate({ id: r.id, rowVersion: r.rowVersion }),
                  });
                }}
                deletable={r.isActive}
              />
            ),
          },
        ] as ColumnDef<SupplierCategoryRecord>[])
      : []),
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary">{rows.length} supplier categories</span>
        {canMutate ? (
          <Button
            variant="primary"
            size="compact"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setShowNew(true)}
          >
            New category
          </Button>
        ) : null}
      </div>
      {del.error ? (
        <Alert tone="danger" className="mb-3">
          {del.error.message}
        </Alert>
      ) : null}
      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No supplier categories yet"
            body="Create supplier categories and optional one-level sub-categories, then assign them to suppliers."
            action={
              canMutate ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add category
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<SupplierCategoryRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>
      {showNew ? (
        <SupplierCategoryModal categories={rows} onClose={() => setShowNew(false)} />
      ) : null}
      {edit ? (
        <SupplierCategoryModal existing={edit} categories={rows} onClose={() => setEdit(null)} />
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel="Deactivate"
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}

function SupplierCategoryModal({
  existing,
  categories,
  onClose,
}: {
  existing?: SupplierCategoryRecord;
  categories: SupplierCategoryRecord[];
  onClose: () => void;
}) {
  const create = useCreateSupplierCategory();
  const update = useUpdateSupplierCategory();
  const [parentCategoryId, setParentCategoryId] = useState(existing?.parentCategoryId ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const mainCategories = categories.filter(
    (category) => category.parentCategoryId === null && category.isActive && category.id !== existing?.id,
  );

  const submit = (): void => {
    if (!name.trim()) return;
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          parentCategoryId: parentCategoryId || null,
          name: name.trim(),
          notes: notes.trim() || null,
          rowVersion: existing.rowVersion,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          parentCategoryId: parentCategoryId || null,
          name: name.trim(),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { onSuccess: onClose },
      );
    }
  };

  const mutation = existing ? update : create;
  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit supplier category' : 'New supplier category'}
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!name.trim()}
            loading={mutation.isPending}
          >
            {existing ? 'Save changes' : 'Create category'}
          </Button>
        </>
      }
    >
      {mutation.error ? (
        <Alert tone="danger" className="mb-3">
          {mutation.error.message}
        </Alert>
      ) : null}
      <FormField label="Parent category">
        <Select value={parentCategoryId} onChange={(e) => setParentCategoryId(e.target.value)}>
          <option value="">Main category</option>
          {mainCategories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Name" required>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>
      <FormField label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Clients panel
// ---------------------------------------------------------------------------

export function ClientsPanel({ canMutate }: { canMutate: boolean }) {
  const list = useClientsListAll();
  const departments = useDepartmentsListAll();
  const del = useDeleteClient();
  const [edit, setEdit] = useState<ClientRecord | null>(null);
  const [view, setView] = useState<ClientRecord | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [docsFor, setDocsFor] = useState<ClientRecord | null>(null);
  // Patch 5 — replace window.confirm
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  if (list.isLoading || departments.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  const allRows = list.data ?? [];
  const departmentRows = departments.data ?? [];
  const needle = q.trim().toLowerCase();
  const rows = allRows.filter((client) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' ? client.isActive : !client.isActive);
    const matchesSearch =
      !needle ||
      [client.name, client.address, client.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    return matchesStatus && matchesSearch;
  });
  const columns: ColumnDef<ClientRecord>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setView(r)}
          className="text-left font-medium text-primary underline-offset-2 hover:underline"
        >
          {r.name}
        </button>
      ),
    },
    {
      id: 'address',
      header: 'Address',
      cell: (r) => <span className="text-secondary">{r.address ?? '—'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
    {
      id: 'view',
      header: '',
      width: '40px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setView(r)}
          title="View client"
          aria-label={`View client ${r.name}`}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
        >
          <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ),
    },
    {
      id: 'docs',
      header: '',
      width: '40px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setDocsFor(r)}
          title="Documents & communications"
          aria-label={`Documents and communications for ${r.name}`}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
        >
          <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ),
    },
    ...(canMutate
      ? ([
          {
            id: 'actions',
            header: '',
            width: '90px',
            cell: (r: ClientRecord) => (
              <RowActions
                onEdit={() => setEdit(r)}
                onDelete={() => {
                  setPendingConfirm({
                    title: 'Deactivate client?',
                    message: `Deactivate client "${r.name}"? They will no longer be selectable for new projects. Existing projects keep their reference.`,
                    onConfirm: () => del.mutate({ id: r.id, rowVersion: r.rowVersion }),
                  });
                }}
                deletable={r.isActive}
              />
            ),
          },
        ] as ColumnDef<ClientRecord>[])
      : []),
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary">
          {rows.length} of {allRows.length} clients
        </span>
        {canMutate ? (
          <Button
            variant="primary"
            size="compact"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setShowNew(true)}
          >
            New client
          </Button>
        ) : null}
      </div>
      {del.error ? (
        <Alert tone="danger" className="mb-3">
          {del.error.message}
        </Alert>
      ) : null}
      <Card className="mb-3" bodyClassName="grid gap-3 md:grid-cols-[minmax(220px,1fr)_160px]">
        <FormField label="Search clients">
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Name, address, notes…"
          />
        </FormField>
        <FormField label="Status">
          <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </Select>
        </FormField>
      </Card>
      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No clients yet"
            body="Clients appear here when added."
            action={
              canMutate ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add client
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<ClientRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>
      {showNew ? <ClientModal onClose={() => setShowNew(false)} /> : null}
      {view ? (
        <ClientDetailDrawer
          client={view}
          departments={departmentRows.filter((department) => department.clientId === view.id)}
          canMutate={canMutate}
          onEdit={() => {
            setEdit(view);
            setView(null);
          }}
          onDocuments={() => {
            setDocsFor(view);
            setView(null);
          }}
          onClose={() => setView(null)}
        />
      ) : null}
      {edit ? <ClientModal existing={edit} onClose={() => setEdit(null)} /> : null}
      {docsFor ? (
        <Drawer
          open={true}
          onClose={() => setDocsFor(null)}
          title={`Documents — ${docsFor.name}`}
          width="wide"
        >
          <MasterEntityDocumentsTab kind="CLIENT" id={docsFor.id} name={docsFor.name} />
        </Drawer>
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel="Deactivate"
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}

function ClientDetailDrawer({
  client,
  departments,
  canMutate,
  onEdit,
  onDocuments,
  onClose,
}: {
  client: ClientRecord;
  departments: DepartmentRecord[];
  canMutate: boolean;
  onEdit: () => void;
  onDocuments: () => void;
  onClose: () => void;
}) {
  const mainDepartments = departments
    .filter((department) => department.parentDepartmentId === null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const subDepartmentsByParent = new Map<string, DepartmentRecord[]>();
  for (const department of departments) {
    if (!department.parentDepartmentId) continue;
    const rows = subDepartmentsByParent.get(department.parentDepartmentId) ?? [];
    rows.push(department);
    subDepartmentsByParent.set(department.parentDepartmentId, rows);
  }
  for (const rows of subDepartmentsByParent.values()) {
    rows.sort((a, b) => a.name.localeCompare(b.name));
  }

  return (
    <Drawer open={true} onClose={onClose} title={`Client — ${client.name}`} width="wide">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">{client.name}</h2>
            <div className="mt-1">
              {client.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="compact" onClick={onDocuments}>
              Documents
            </Button>
            {canMutate ? (
              <Button variant="primary" size="compact" onClick={onEdit}>
                Edit client
              </Button>
            ) : null}
          </div>
        </div>

        <Card bodyClassName="grid gap-4 md:grid-cols-2">
          <DetailItem label="Address" value={client.address} />
          <DetailItem label="Notes" value={client.notes} />
          <DetailItem label="Created" value={new Date(client.createdAt).toLocaleString()} />
          <DetailItem label="Last updated" value={new Date(client.updatedAt).toLocaleString()} />
        </Card>

        <Card>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-primary">Departments & sub-departments</h3>
            <span className="text-xs text-secondary">{departments.length} records</span>
          </div>
          {mainDepartments.length === 0 ? (
            <p className="text-sm text-secondary">No departments have been added for this client yet.</p>
          ) : (
            <div className="space-y-3">
              {mainDepartments.map((department) => {
                const children = subDepartmentsByParent.get(department.id) ?? [];
                return (
                  <div key={department.id} className="rounded-md border border-subtle p-3">
                    <DepartmentSummary department={department} />
                    {children.length > 0 ? (
                      <div className="mt-3 space-y-2 border-l border-subtle pl-3">
                        {children.map((child) => (
                          <DepartmentSummary key={child.id} department={child} compact />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </Drawer>
  );
}

function DepartmentSummary({
  department,
  compact = false,
}: {
  department: DepartmentRecord;
  compact?: boolean;
}) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={compact ? 'text-sm font-medium text-primary' : 'font-medium text-primary'}>
          {compact ? '↳ ' : ''}
          {department.name}
        </span>
        {department.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
      </div>
      <dl className="mt-2 grid gap-2 text-xs text-secondary md:grid-cols-2">
        <DetailItem label="Address" value={department.address} />
        <DetailItem label="Contact / office" value={department.contactName} />
        <DetailItem label="Phone" value={department.phone} />
        <DetailItem label="Email" value={department.email} />
        <DetailItem label="Fax" value={department.fax} />
        <DetailItem label="Notes" value={department.notes} />
      </dl>
    </div>
  );
}

function ClientModal({ existing, onClose }: { existing?: ClientRecord; onClose: () => void }) {
  const create = useCreateClient();
  const update = useUpdateClient();
  const [name, setName] = useState(existing?.name ?? '');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const submit = (): void => {
    if (!name.trim()) return;
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          name: name.trim(),
          address: address.trim() || null,
          notes: notes.trim() || null,
          rowVersion: existing.rowVersion,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          name: name.trim(),
          ...(address.trim() ? { address: address.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { onSuccess: onClose },
      );
    }
  };

  const mutation = existing ? update : create;
  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit client' : 'New client'}
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!name.trim()}
            loading={mutation.isPending}
          >
            {existing ? 'Save changes' : 'Create client'}
          </Button>
        </>
      }
    >
      {mutation.error ? (
        <Alert tone="danger" className="mb-3">
          {mutation.error.message}
        </Alert>
      ) : null}
      <FormField label="Name" required>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>
      <FormField label="Address">
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
      </FormField>
      <FormField label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Departments panel
// ---------------------------------------------------------------------------

export function DepartmentsPanel({ canMutate }: { canMutate: boolean }) {
  const list = useDepartmentsListAll();
  const clients = useClientsListAll();
  const del = useDeleteDepartment();
  const [edit, setEdit] = useState<DepartmentRecord | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [docsFor, setDocsFor] = useState<DepartmentRecord | null>(null);
  // Patch 5
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [q, setQ] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

  if (list.isLoading || clients.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }
  const allRows = list.data ?? [];
  const clientRows = clients.data ?? [];
  const clientsById = new Map(clientRows.map((client) => [client.id, client]));
  const departmentsById = new Map(allRows.map((department) => [department.id, department]));
  const needle = q.trim().toLowerCase();
  const rows = allRows.filter((department) => {
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' ? department.isActive : !department.isActive);
    const matchesClient = !clientFilter || department.clientId === clientFilter;
    const matchesSearch =
      !needle ||
      [
        department.name,
        department.contactName,
        department.address,
        department.phone,
        department.email,
        department.fax,
        department.notes,
        department.clientId ? clientsById.get(department.clientId)?.name : null,
        department.parentDepartmentId ? departmentsById.get(department.parentDepartmentId)?.name : null,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    return matchesStatus && matchesClient && matchesSearch;
  });

  const columns: ColumnDef<DepartmentRecord>[] = [
    {
      id: 'name',
      header: 'Department / sub-department',
      cell: (r) => (
        <span className="font-medium">
          {r.parentDepartmentId ? '↳ ' : ''}
          {r.name}
        </span>
      ),
    },
    {
      id: 'client',
      header: 'Client',
      cell: (r) => <span className="text-secondary">{r.clientId ? clientsById.get(r.clientId)?.name ?? '—' : '—'}</span>,
    },
    {
      id: 'parent',
      header: 'Parent',
      cell: (r) => (
        <span className="text-secondary">
          {r.parentDepartmentId ? departmentsById.get(r.parentDepartmentId)?.name ?? '—' : 'Main department'}
        </span>
      ),
    },
    {
      id: 'contact',
      header: 'Contact',
      cell: (r) => (
        <div className="text-secondary">
          {r.contactName ?? '—'}
          {r.address ? <span className="block text-xs text-secondary">Address: {r.address}</span> : null}
          {r.phone ? <span className="block text-xs text-secondary">Phone: {r.phone}</span> : null}
          {r.email ? <span className="block text-xs text-secondary">Email: {r.email}</span> : null}
          {r.fax ? <span className="block text-xs text-secondary">Fax: {r.fax}</span> : null}
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
    {
      id: 'docs',
      header: '',
      width: '40px',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setDocsFor(r)}
          title="Documents & communications"
          aria-label={`Documents and communications for ${r.name}`}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
        >
          <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ),
    },
    ...(canMutate
      ? ([
          {
            id: 'actions',
            header: '',
            width: '90px',
            cell: (r: DepartmentRecord) => (
              <RowActions
                onEdit={() => setEdit(r)}
                onDelete={() => {
                  setPendingConfirm({
                    title: 'Deactivate department?',
                    message: `Deactivate department "${r.name}"? It will no longer be selectable for new project assignments. Existing references keep the name snapshot.`,
                    onConfirm: () => del.mutate({ id: r.id, rowVersion: r.rowVersion }),
                  });
                }}
                deletable={r.isActive}
              />
            ),
          },
        ] as ColumnDef<DepartmentRecord>[])
      : []),
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary">
          {rows.length} of {allRows.length} client-wise departments
        </span>
        {canMutate ? (
          <Button
            variant="primary"
            size="compact"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setShowNew(true)}
            disabled={clientRows.length === 0}
          >
            New department
          </Button>
        ) : null}
      </div>
      {clientRows.length === 0 ? (
        <Alert tone="warning" className="mb-3">
          Add a client first. Departments and sub-departments are maintained under each client.
        </Alert>
      ) : null}
      {del.error ? (
        <Alert tone="danger" className="mb-3">
          {del.error.message}
        </Alert>
      ) : null}
      <Card className="mb-3" bodyClassName="grid gap-3 md:grid-cols-[minmax(220px,1fr)_220px_140px]">
        <FormField label="Search departments">
          <Input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Name, client, contact, address…"
          />
        </FormField>
        <FormField label="Client">
          <Select value={clientFilter} onChange={(event) => setClientFilter(event.target.value)}>
            <option value="">All clients</option>
            {clientRows.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Status">
          <Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </Select>
        </FormField>
      </Card>
      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No departments yet"
            body="Departments are now maintained client-wise. Add a department under a client, then optionally add sub-departments beneath it."
            action={
              canMutate && clientRows.length > 0 ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add department
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<DepartmentRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>
      {showNew ? (
        <DepartmentModal
          departments={allRows}
          clients={clientRows}
          onClose={() => setShowNew(false)}
        />
      ) : null}
      {edit ? (
        <DepartmentModal
          existing={edit}
          departments={allRows}
          clients={clientRows}
          onClose={() => setEdit(null)}
        />
      ) : null}
      {docsFor ? (
        <Drawer
          open={true}
          onClose={() => setDocsFor(null)}
          title={`Documents — ${docsFor.name}`}
          width="wide"
        >
          <MasterEntityDocumentsTab kind="DEPARTMENT" id={docsFor.id} name={docsFor.name} />
        </Drawer>
      ) : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel="Deactivate"
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}

function DepartmentModal({
  existing,
  departments,
  clients,
  onClose,
}: {
  existing?: DepartmentRecord;
  departments: DepartmentRecord[];
  clients: ClientRecord[];
  onClose: () => void;
}) {
  const create = useCreateDepartment();
  const update = useUpdateDepartment();
  const [clientId, setClientId] = useState(existing?.clientId ?? clients[0]?.id ?? '');
  const [parentDepartmentId, setParentDepartmentId] = useState(existing?.parentDepartmentId ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [contactName, setContactName] = useState(existing?.contactName ?? '');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [fax, setFax] = useState(existing?.fax ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const parentOptions = departments.filter(
    (department) =>
      department.clientId === clientId &&
      department.parentDepartmentId === null &&
      department.id !== existing?.id,
  );

  const submit = (): void => {
    if (!name.trim() || !clientId) return;
    const payload = {
      clientId,
      parentDepartmentId: parentDepartmentId || null,
      name: name.trim(),
      contactName: contactName.trim() || null,
      address: address.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      fax: fax.trim() || null,
      notes: notes.trim() || null,
    };
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          ...payload,
          rowVersion: existing.rowVersion,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          ...payload,
          contactName: payload.contactName ?? undefined,
          address: payload.address ?? undefined,
          phone: payload.phone ?? undefined,
          email: payload.email ?? undefined,
          fax: payload.fax ?? undefined,
          notes: payload.notes ?? undefined,
        },
        { onSuccess: onClose },
      );
    }
  };

  const mutation = existing ? update : create;
  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit department' : 'New department'}
      size="md"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!name.trim() || !clientId}
            loading={mutation.isPending}
          >
            {existing ? 'Save changes' : 'Create department'}
          </Button>
        </>
      }
    >
      {mutation.error ? (
        <Alert tone="danger" className="mb-3">
          {mutation.error.message}
        </Alert>
      ) : null}
      <FormField label="Client" required>
        <Select
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            setParentDepartmentId('');
          }}
        >
          <option value="" disabled>
            Select client
          </option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Parent department">
        <Select
          value={parentDepartmentId}
          onChange={(e) => setParentDepartmentId(e.target.value)}
          disabled={!clientId}
        >
          <option value="">Main department</option>
          {parentOptions.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Name" required>
        <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </FormField>
      <FormField label="Contact person / office name">
        <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
      </FormField>
      <FormField label="Address">
        <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} />
      </FormField>
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Phone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </FormField>
        <FormField label="Email">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label="Fax">
          <Input value={fax} onChange={(e) => setFax(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Incoterms panel
// ---------------------------------------------------------------------------

function IncotermsPanel({ canMutate }: { canMutate: boolean }) {
  const list = useIncotermsListAll();
  const del = useDeleteIncoterm();
  const [edit, setEdit] = useState<IncotermRecord | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  if (list.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const rows = list.data ?? [];
  const columns: ColumnDef<IncotermRecord>[] = [
    { id: 'code', header: 'Code', width: '120px', cell: (r) => <span className="font-medium">{r.code}</span> },
    {
      id: 'label',
      header: 'Label',
      cell: (r) => <span className="text-secondary">{r.label ?? '—'}</span>,
    },
    {
      id: 'notes',
      header: 'Notes',
      cell: (r) => <span className="text-secondary">{r.notes ?? '—'}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      width: '110px',
      cell: (r) =>
        r.isActive ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>,
    },
    ...(canMutate
      ? ([
          {
            id: 'actions',
            header: '',
            width: '90px',
            cell: (r: IncotermRecord) => (
              <RowActions
                onEdit={() => setEdit(r)}
                onDelete={() => {
                  setPendingConfirm({
                    title: 'Deactivate incoterm?',
                    message: `Deactivate incoterm "${r.code}"? It will no longer be selectable for new project terms of delivery. Existing projects keep their saved value.`,
                    onConfirm: () => del.mutate({ id: r.id, rowVersion: r.rowVersion }),
                  });
                }}
                deletable={r.isActive}
              />
            ),
          },
        ] as ColumnDef<IncotermRecord>[])
      : []),
  ];

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm text-secondary">{rows.length} incoterms</span>
        {canMutate ? (
          <Button
            variant="primary"
            size="compact"
            leftIcon={<Plus className="h-3.5 w-3.5" />}
            onClick={() => setShowNew(true)}
          >
            New incoterm
          </Button>
        ) : null}
      </div>
      {del.error ? (
        <Alert tone="danger" className="mb-3">
          {del.error.message}
        </Alert>
      ) : null}
      <Card bodyClassName="p-0">
        {rows.length === 0 ? (
          <EmptyState
            title="No incoterms yet"
            body="Incoterms become selectable in General Particulars after they are added here. FOR is still limited to PKR projects."
            action={
              canMutate ? (
                <Button onClick={() => setShowNew(true)}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add incoterm
                </Button>
              ) : null
            }
          />
        ) : (
          <DataTable<IncotermRecord> columns={columns} rows={rows} rowKey={(r) => r.id} />
        )}
      </Card>
      {showNew ? <IncotermModal onClose={() => setShowNew(false)} /> : null}
      {edit ? <IncotermModal existing={edit} onClose={() => setEdit(null)} /> : null}

      {pendingConfirm ? (
        <WarningConfirmDialog
          open
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel="Deactivate"
          pending={del.isPending}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => {
            pendingConfirm.onConfirm();
            setPendingConfirm(null);
          }}
        />
      ) : null}
    </>
  );
}

function IncotermModal({
  existing,
  onClose,
}: {
  existing?: IncotermRecord;
  onClose: () => void;
}) {
  const create = useCreateIncoterm();
  const update = useUpdateIncoterm();
  const [code, setCode] = useState(existing?.code ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');

  const submit = (): void => {
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) return;
    if (existing) {
      update.mutate(
        {
          id: existing.id,
          code: cleanCode,
          label: label.trim() || null,
          notes: notes.trim() || null,
          rowVersion: existing.rowVersion,
        },
        { onSuccess: onClose },
      );
    } else {
      create.mutate(
        {
          code: cleanCode,
          ...(label.trim() ? { label: label.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
        { onSuccess: onClose },
      );
    }
  };

  const mutation = existing ? update : create;
  return (
    <Modal
      open
      onClose={onClose}
      title={existing ? 'Edit incoterm' : 'New incoterm'}
      size="sm"
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!code.trim()}
            loading={mutation.isPending}
          >
            {existing ? 'Save changes' : 'Create incoterm'}
          </Button>
        </>
      }
    >
      {mutation.error ? (
        <Alert tone="danger" className="mb-3">
          {mutation.error.message}
        </Alert>
      ) : null}
      <FormField label="Code" required>
        <Input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. CIF" />
      </FormField>
      <FormField label="Label">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Cost, Insurance and Freight" />
      </FormField>
      <FormField label="Notes">
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </FormField>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Shared row actions cell
// ---------------------------------------------------------------------------

function RowActions({
  onEdit,
  onDelete,
  deletable,
}: {
  onEdit: () => void;
  onDelete: () => void;
  deletable: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        aria-label="Edit"
        onClick={onEdit}
        className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-primary"
      >
        <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} />
      </button>
      {deletable ? (
        <button
          type="button"
          aria-label="Deactivate"
          onClick={onDelete}
          className="rounded-sm p-1 text-tertiary hover:bg-subtle hover:text-status-danger"
        >
          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      ) : null}
    </div>
  );
}
